import { authApi } from "./index";

// Local storage keys for client-side fallbacks
const LOCAL_REGISTRATIONS_KEY = "amalgamate_local_event_registrations";
const LOCAL_WAITLIST_KEY = "amalgamate_local_waitlist_entries";

const getLocalRegistrations = () => {
  const data = localStorage.getItem(LOCAL_REGISTRATIONS_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLocalRegistrations = (regs) => {
  localStorage.setItem(LOCAL_REGISTRATIONS_KEY, JSON.stringify(regs));
};

const getLocalWaitlist = () => {
  const data = localStorage.getItem(LOCAL_WAITLIST_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLocalWaitlist = (entries) => {
  localStorage.setItem(LOCAL_WAITLIST_KEY, JSON.stringify(entries));
};

// --- API METHODS ---

/**
 * Register for an event
 */
export const registerForEvent = async (offeringId, { ticketCount, attendeeNames, specialRequirements, shippingAddressLine, shippingCity, shippingState, shippingZipcode, shippingCountry }) => {
  try {
    const response = await authApi.post(`/Events/${offeringId}/register`, {
      ticketCount,
      attendeeNames,
      specialRequirements,
      shippingAddressLine,
      shippingCity,
      shippingState,
      shippingZipcode,
      shippingCountry
    });
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API POST /Events/.../register not reachable. Simulating locally.", error);
    
    // Simulate locally
    const currentUserId = localStorage.getItem("userId") || "9fa85f64-5717-4562-b3fc-2c963f66afa6";
    const regs = getLocalRegistrations();
    const waitlist = getLocalWaitlist();
    
    // Fetch local offerings to get max capacity
    const localOfferings = JSON.parse(localStorage.getItem("amalgamate_local_offerings_v3") || "[]");
    const offering = localOfferings.find(o => o.offeringId === offeringId) || {
      title: "Local Event",
      maxCapacity: 10,
      waitlistEnabled: true,
      maxTicketsPerPerson: 5
    };

    // Calculate spots filled
    const spotsFilled = regs
      .filter(r => r.offeringId === offeringId && r.status === "registered")
      .reduce((sum, r) => sum + r.ticketCount, 0);

    const remaining = (offering.maxCapacity || 10) - spotsFilled;
    const shouldWaitlist = remaining < ticketCount;

    const newId = 'reg_' + Math.random().toString(36).substr(2, 9);

    if (shouldWaitlist) {
      if (!offering.waitlistEnabled) {
        throw new Error("This event is sold out and waitlisting is not enabled.");
      }

      // Check if already waitlisted
      const alreadyWaitlisted = waitlist.some(w => w.offeringId === offeringId && w.customerId === currentUserId);
      if (alreadyWaitlisted) {
        throw new Error("You are already on the waitlist for this event.");
      }

      // Add to registrations
      const newReg = {
        registrationId: newId,
        offeringId,
        customerId: currentUserId,
        ticketCount,
        attendeeNames,
        specialRequirements,
        status: "waitlisted",
        createdAt: new Date().toISOString(),
        cancelledAt: null,
        offeringTitle: offering.title,
        customerName: "Current User",
        shippingAddressLine,
        shippingCity,
        shippingState,
        shippingZipcode,
        shippingCountry
      };

      // Add to waitlist
      const eventWaitlist = waitlist.filter(w => w.offeringId === offeringId);
      const newPos = eventWaitlist.length + 1;
      const newWait = {
        waitlistId: 'wait_' + Math.random().toString(36).substr(2, 9),
        offeringId,
        customerId: currentUserId,
        position: newPos,
        createdAt: new Date().toISOString(),
        customerName: "Current User"
      };

      regs.unshift(newReg);
      waitlist.push(newWait);
      
      saveLocalRegistrations(regs);
      saveLocalWaitlist(waitlist);

      return {
        registrationId: newId,
        offeringId,
        customerId: currentUserId,
        ticketCount,
        status: "waitlisted",
        waitlisted: true,
        waitlistPosition: newPos,
        createdAt: newReg.createdAt,
        shippingAddressLine,
        shippingCity,
        shippingState,
        shippingZipcode,
        shippingCountry
      };
    } else {
      // Register successfully
      const newReg = {
        registrationId: newId,
        offeringId,
        customerId: currentUserId,
        ticketCount,
        attendeeNames,
        specialRequirements,
        status: "registered",
        createdAt: new Date().toISOString(),
        cancelledAt: null,
        offeringTitle: offering.title,
        customerName: "Current User",
        shippingAddressLine,
        shippingCity,
        shippingState,
        shippingZipcode,
        shippingCountry
      };

      regs.unshift(newReg);
      saveLocalRegistrations(regs);

      return {
        registrationId: newId,
        offeringId,
        customerId: currentUserId,
        ticketCount,
        status: "registered",
        waitlisted: false,
        createdAt: newReg.createdAt,
        shippingAddressLine,
        shippingCity,
        shippingState,
        shippingZipcode,
        shippingCountry
      };
    }
  }
};

/**
 * Cancel registration (Customer or Provider)
 */
export const cancelEventRegistration = async (regId) => {
  try {
    const response = await authApi.delete(`/Events/registrations/${regId}`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API DELETE /Events/registrations/... not reachable. Simulating locally.", error);
    
    const regs = getLocalRegistrations();
    const waitlist = getLocalWaitlist();
    
    const reg = regs.find(r => r.registrationId === regId);
    if (!reg) throw new Error("Registration not found.");
    if (reg.status === "cancelled") throw new Error("Registration already cancelled.");

    const previousStatus = reg.status;
    reg.status = "cancelled";
    reg.cancelledAt = new Date().toISOString();

    const offeringId = reg.offeringId;

    if (previousStatus === "waitlisted") {
      const waitEntryIndex = waitlist.findIndex(w => w.offeringId === offeringId && w.customerId === reg.customerId);
      if (waitEntryIndex !== -1) {
        const removedPos = waitlist[waitEntryIndex].position;
        waitlist.splice(waitEntryIndex, 1);
        
        // Shift remaining waitlist positions
        waitlist
          .filter(w => w.offeringId === offeringId && w.position > removedPos)
          .forEach(w => w.position--);
      }
    } else if (previousStatus === "registered") {
      // Promote first person on waitlist
      const eventWaitlist = waitlist
        .filter(w => w.offeringId === offeringId)
        .sort((a, b) => a.position - b.position);

      if (eventWaitlist.length > 0) {
        const firstWait = eventWaitlist[0];
        const waitlistReg = regs.find(r => r.offeringId === offeringId && r.customerId === firstWait.customerId && r.status === "waitlisted");
        
        if (waitlistReg) {
          waitlistReg.status = "registered";
          
          // Remove waitlist entry
          const waitIndex = waitlist.findIndex(w => w.waitlistId === firstWait.waitlistId);
          if (waitIndex !== -1) {
            waitlist.splice(waitIndex, 1);
          }
          
          // Shift positions
          waitlist
            .filter(w => w.offeringId === offeringId && w.position > firstWait.position)
            .forEach(w => w.position--);
        }
      }
    }

    saveLocalRegistrations(regs);
    saveLocalWaitlist(waitlist);
    return true;
  }
};

/**
 * Get all registrations for an event (Provider only)
 */
export const getEventRegistrations = async (offeringId) => {
  try {
    const response = await authApi.get(`/Events/${offeringId}/registrations`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API GET /Events/.../registrations not reachable. Simulating locally.", error);
    
    const regs = getLocalRegistrations();
    return regs.filter(r => r.offeringId === offeringId);
  }
};

/**
 * Get waitlist for an event (Provider only)
 */
export const getEventWaitlist = async (offeringId) => {
  try {
    const response = await authApi.get(`/Events/${offeringId}/waitlist`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API GET /Events/.../waitlist not reachable. Simulating locally.", error);
    
    const waitlist = getLocalWaitlist();
    return waitlist
      .filter(w => w.offeringId === offeringId)
      .sort((a, b) => a.position - b.position);
  }
};

/**
 * Cancel an event (Provider only)
 */
export const cancelEvent = async (offeringId, messageToAttendees = "") => {
  try {
    const response = await authApi.patch(`/Events/${offeringId}/cancel`, {
      messageToAttendees
    });
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API PATCH /Events/.../cancel not reachable. Simulating locally.", error);
    
    // Update local offering status to draft
    const localOfferings = JSON.parse(localStorage.getItem("amalgamate_local_offerings_v3") || "[]");
    const offeringIndex = localOfferings.findIndex(o => o.offeringId === offeringId);
    if (offeringIndex !== -1) {
      localOfferings[offeringIndex].status = "draft";
      localStorage.setItem("amalgamate_local_offerings_v3", JSON.stringify(localOfferings));
    }

    // Cancel registrations
    const regs = getLocalRegistrations();
    regs
      .filter(r => r.offeringId === offeringId && r.status !== "cancelled")
      .forEach(r => {
        r.status = "cancelled";
        r.cancelledAt = new Date().toISOString();
      });
    
    // Clear waitlist
    const waitlist = getLocalWaitlist().filter(w => w.offeringId !== offeringId);

    saveLocalRegistrations(regs);
    saveLocalWaitlist(waitlist);
    return true;
  }
};

/**
 * Get customer's own registrations
 */
export const getMyRegistrations = async () => {
  try {
    const response = await authApi.get(`/Events/my-registrations`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API GET /Events/my-registrations not reachable. Simulating locally.", error);
    
    const regs = getLocalRegistrations();
    const currentUserId = localStorage.getItem("userId") || "9fa85f64-5717-4562-b3fc-2c963f66afa6";
    return regs.filter(r => r.customerId === currentUserId);
  }
};

/**
 * Get all event registrations for a provider (Provider only)
 */
export const getProviderEventRegistrations = async () => {
  try {
    const response = await authApi.get(`/Events/provider-registrations`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API GET /Events/provider-registrations not reachable. Simulating locally.", error);
    const regs = getLocalRegistrations();
    return regs;
  }
};
